import {
    useQueries,
    useQueryClient,
    type UseQueryResult,
} from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { FinancialDocumentDetailIncludeQuery } from "@common/schemas/financialDocument/financial-document-detail-include.schema";
import { fetchFinancialDocument } from "@lib/financialDocument";
import { financialDocumentQueryKeys } from "./lib";
import {
    useFinancialDocumentsQuery,
    UseFinancialDocumentsQueryParams,
    REFETCH_INTERVAL_MS,
} from "./useFinancialDocumentsQuery";

interface UseFinancialDocumentsExtendedQueryParams extends UseFinancialDocumentsQueryParams {
    detailIncludes?: FinancialDocumentDetailIncludeQuery;
}

interface CombinedFinancialDocumentDetails {
    details: FinancialDocumentExtended[];
    isAnyPending: boolean;
    isFetching: boolean;
    isError: boolean;
    hasDetailFetchFailures: boolean;
    detailsError: Error | null;
    settledDetailCount: number;
}

function combineFinancialDocumentDetailQueries(
    results: Array<UseQueryResult<FinancialDocumentExtended, Error>>,
): CombinedFinancialDocumentDetails {
    const details = results
        .map((query) => query.data)
        .filter(
            (document): document is FinancialDocumentExtended =>
                document !== undefined,
        );
    const failedQueries = results.filter((query) => query.isError);

    return {
        details,
        isAnyPending: results.some((query) => query.isPending),
        isFetching: results.some((query) => query.isFetching),
        isError:
            failedQueries.length > 0 &&
            !results.some((query) => query.data !== undefined),
        hasDetailFetchFailures: failedQueries.length > 0,
        detailsError: failedQueries[0]?.error ?? null,
        settledDetailCount: details.length + failedQueries.length,
    };
}

export function useFinancialDocumentsExtendedQuery(
    params: UseFinancialDocumentsExtendedQueryParams,
) {
    const queryClient = useQueryClient();
    const { shouldRefetch, detailIncludes, ...listQueryParams } = params;
    const listQuery = useFinancialDocumentsQuery({
        shouldRefetch,
        ...listQueryParams,
    });
    const listDocuments = listQuery.data?.data ?? [];
    const shouldPollDetails = shouldRefetch?.(listQuery.data) ?? false;
    const resolvedDetailIncludes = useMemo(
        () => detailIncludes ?? {},
        [detailIncludes],
    );

    const {
        details,
        isAnyPending,
        isFetching: isDetailsFetching,
        isError: isDetailsError,
        hasDetailFetchFailures,
        detailsError,
        settledDetailCount,
    } = useQueries({
        queries: listDocuments.map((document) => ({
            queryKey: financialDocumentQueryKeys.detail(
                document.id,
                resolvedDetailIncludes,
            ),
            queryFn: () =>
                fetchFinancialDocument(document.id, resolvedDetailIncludes),
            enabled: listQuery.isSuccess,
            refetchInterval: shouldPollDetails ? REFETCH_INTERVAL_MS : false,
        })),
        combine: combineFinancialDocumentDetailQueries,
    });

    const isDetailsPending =
        listDocuments.length > 0 &&
        (isAnyPending || settledDetailCount < listDocuments.length);

    const extendedDocuments = useMemo(() => {
        if (!listQuery.data || isDetailsPending) {
            return undefined;
        }

        if (listDocuments.length === 0) {
            return {
                ...listQuery.data,
                data: [],
            };
        }

        if (details.length === 0) {
            return undefined;
        }

        return {
            ...listQuery.data,
            data: details,
        };
    }, [details, isDetailsPending, listDocuments.length, listQuery.data]);

    const refetchList = listQuery.refetch;

    const refetch = useCallback(async () => {
        const listResult = await refetchList();
        const documents = listResult.data?.data ?? [];

        await Promise.allSettled(
            documents.map((document) =>
                queryClient.fetchQuery({
                    queryKey: financialDocumentQueryKeys.detail(
                        document.id,
                        resolvedDetailIncludes,
                    ),
                    queryFn: () =>
                        fetchFinancialDocument(
                            document.id,
                            resolvedDetailIncludes,
                        ),
                }),
            ),
        );

        return listResult;
    }, [refetchList, queryClient, resolvedDetailIncludes]);

    return {
        ...listQuery,
        data: extendedDocuments,
        isPending: listQuery.isPending || isDetailsPending,
        isLoading: listQuery.isLoading || isDetailsPending,
        isError: listQuery.isError || isDetailsError || hasDetailFetchFailures,
        error: listQuery.error ?? detailsError,
        isFetching: listQuery.isFetching || isDetailsFetching,
        refetch,
    };
}
